/* eslint-disable react/prop-types, @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';

import { AMPLITUDE_SERVICE } from '#analytics/constants.js';
import { trackingEvent } from '#analytics/index.js';
import { getBasicAmplitudEventProperties } from '#analytics/utils.js';
import DatePickerSystem from '#components/DesignSystem/DatePickerSystem/index.js';
import DropdownSystem from '#components/DesignSystem/DropdownSystem/index.js';
import ModalSystem from '#components/DesignSystem/ModalSystem/index.js';
import { NUMWEEKSPERPAGE } from '#components/Report/LookAHeadReport/index.js';
import { PdfIcon, WarningDiamontIcon } from '#icons/index.js';
import { Spin } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useLookaheadDailyPdfExportValidation } from './hooks/useLookaheadDailyPdfExportValidation.js';

export function ReportModal(props) {
  const { modelReportLookAhead, setModelReportLookAhead, t } = props;
  return (
    <ModalSystem
      theme="dark"
      width={480}
      title={t('export_lookahead_pdf_modal.title')}
      showIconTitle={true}
      iconTitle={<PdfIcon color="#7DFF8A" />}
      visible={modelReportLookAhead}
      setVisible={setModelReportLookAhead}
      centered={true}
      onCancelFunction={setModelReportLookAhead}>
      <ExportPdfContentModal {...props} />
    </ModalSystem>
  );
}

function ExportPdfContentModal({
  activities,
  antIcon,
  defaultView,
  history,
  modelReportLookAhead,
  setModelReportLookAhead,
  t
}) {
  const projectState: any = useSelector<any>((state) => state.projectState);
  const [numWeeks, setNumWeeks] = useState(NUMWEEKSPERPAGE);

  const [modelReportDateLookAhead, setModelReportDateLookAhead] = useState(
    moment(new Date(), 'YYYY/MM/DD').format('YYYY/MM/DD')
  );

  const config = {
    sector_id: projectState.sectorSelected,
    start: modelReportDateLookAhead,
    end: moment(modelReportDateLookAhead)
      .add(numWeeks * 7 - 1, 'days')
      .format('YYYY/MM/DD')
  };

  const {
    isLoadingPdf,
    setLoadingPdf,
    isShowAlertEmpty,
    isErrorForm,
    setIsErrorForm,
    validateAndNavigate
  } = useLookaheadDailyPdfExportValidation({
    activities,
    modelReportDateLookAhead,
    numWeeks,
    defaultView,
    history,
    config
  });

  const handleCloseModal = () => {
    trackingEvent(
      'lookahead_pdf_export_cancelation',
      {
        ...getBasicAmplitudEventProperties(),
        start_date_selected: modelReportDateLookAhead,
        weeks_number_selected: numWeeks
      },
      AMPLITUDE_SERVICE
    );
    setLoadingPdf(false);
    setModelReportLookAhead(!modelReportLookAhead);
  };

  return (
    <div className="modal--export--pdf">
      {isShowAlertEmpty && (
        <div className="modal--export--pdf__alert">
          <div className="alert__icon">
            <WarningDiamontIcon color="#FDD551" />
          </div>
          <div className="alert__text">
            <p>{t('export_lookahead_pdf_modal.alert_text')}</p>
          </div>
        </div>
      )}
      <div className="modal--export--pdf__description">
        <label>{t('export_lookahead_pdf_modal.description')}</label>
      </div>
      <div className="modal--export--pdf__options">
        <PrintDateSelectOption
          isErrorForm={isErrorForm}
          setIsErrorForm={setIsErrorForm}
          setModelReportDateLookAhead={setModelReportDateLookAhead}
          t={t}
        />
        <PrintNumWeeksOption
          isErrorForm={isErrorForm}
          numWeeks={numWeeks}
          setIsErrorForm={setIsErrorForm}
          setNumWeeks={setNumWeeks}
          t={t}
        />
      </div>
      <div className="modal--export--pdf__footer">
        <button className="footer__cancel" onClick={handleCloseModal}>
          {t('cancel')}
        </button>
        <button
          className="footer__download"
          disabled={isLoadingPdf}
          onClick={validateAndNavigate}
          key="submit"
          style={{
            background: '#7DFF8A',
            color: '#121212',
            borderColor: '#7DFF8A'
          }}>
          {isLoadingPdf ? (
            <Spin indicator={antIcon} className="download__spinner" />
          ) : (
            t('export_lookahead_pdf_modal.generate')
          )}
        </button>
      </div>
    </div>
  );
}

function PrintDateSelectOption({
  isErrorForm,
  setIsErrorForm,
  setModelReportDateLookAhead,
  t
}) {
  const dateFormatDefault = 'YYYY/MM/DD';
  const formatGantt =
    window.ganttVisualization?.currentDateFormat || dateFormatDefault;
  const formatCorrect = formatGantt.split(' ')[0];

  const dateReportHandle = async (_date) => {
    const dateString = moment(_date).format('YYYY/MM/DD');
    setModelReportDateLookAhead(dateString);
    setIsErrorForm(false);
  };

  return (
    <div className="option-print--datepicker">
      <label>{t('export_lookahead_pdf_modal.start_date')}</label>
      <div className="select__picker">
        <DatePickerSystem
          theme="dark"
          format={formatCorrect}
          defaultValue={moment(new Date(), formatCorrect)}
          defaultPickerValue={moment(new Date(), formatCorrect)}
          allowClear={false}
          onChange={(_date) => dateReportHandle(_date)}
          isError={isErrorForm}
        />
      </div>
    </div>
  );
}

function PrintNumWeeksOption({
  isErrorForm,
  numWeeks,
  setIsErrorForm,
  setNumWeeks,
  t
}) {
  const generateWeekItems = () => {
    const numWeeksRange = Array.from({ length: 8 }, (_, index) => index + 1);

    return numWeeksRange.map((value) => ({
      key: value,
      label: `${value} ${value === 1 ? t('export_lookahead_pdf_modal.week') : t('export_lookahead_pdf_modal.weeks')}`,
      onClick: () => {
        setNumWeeks(value);
        setIsErrorForm(false);
      },
      disabled: false,
      isDivider: false,
      active: value === numWeeks,
      subitems: []
    }));
  };

  return (
    <div className="option-print--weeks">
      <label>{t('export_lookahead_pdf_modal.num_weeks')}</label>
      <div className="format__dropdown">
        <DropdownSystem
          theme="dark"
          version="v2"
          trigger={['click']}
          height={32}
          label={`${numWeeks} ${numWeeks === 1 ? t('export_lookahead_pdf_modal.week') : t('export_lookahead_pdf_modal.weeks')}`}
          withRadioButtons={false}
          items={generateWeekItems()}
          isError={isErrorForm}
        />
      </div>
    </div>
  );
}
